import React, { useEffect, useState } from 'react';
import {
    Button,
    TextField,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from "@mui/material";
import { Dialog } from "@mui/material";
import {
    AccountAddress,
    useAddAddressMutation,
    useGetAddressesQuery,
    useUpdateAddressMutation,
} from "../../../services/grace";
import { DialogProps } from "@mui/material/Dialog/Dialog";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useAlertDispatch } from '../../../context/AlertContext/AlertContext';

export interface AddressDialogProps extends DialogProps {
    accountId: string;
    onClose: () => void;
    onSubmit: () => void;
    title: string;
    addressId?: string;
}

export default function AddressDialog(props: AddressDialogProps): JSX.Element {
    const {
        onClose,
        onSubmit,
        accountId,
        addressId,
        title,
        ...other
    } = props;
    const alertDispatch = useAlertDispatch();

    const {
        data: addresses
    } = useGetAddressesQuery(accountId);
    const [addAddress] = useAddAddressMutation();
    const [updateAddress] = useUpdateAddressMutation();

    const [formValues, setFormValues] = useState<Omit<AccountAddress, "id">>({
        description: "",
        contact: "",
        line1: "",
        line2: "",
        line3: "",
        phone: "",
    });

    const [hasDupeDescription, setHasDupeDescription] = useState<boolean>(false);
    const [inProgress, setInProgress] = useState(false);

    useEffect(() => {
        const isDescriptionExists = addresses?.some(address =>
            address.id !== addressId && address.description.toLowerCase() === formValues.description.trim().toLowerCase());
        setHasDupeDescription(isDescriptionExists || false);
    }, [formValues.description, addresses, addressId]);

    useEffect(() => {
        if (addressId) {
            addresses?.forEach(address => address.id === addressId && setFormValues({ ...address }))
        }
    }, [addresses, addressId])

    const handleClose = () => {
        if (!inProgress) {
            onClose();
        }
    };

    const handleSubmit = async () => {
        if (!formValues.description.trim()) {
            return;
        }

        try {
            setInProgress(true);

            if (addressId) {
                await updateAddress({
                    ...formValues,
                    id: addressId,
                    accountId
                }).unwrap();
            } else {
                await addAddress({ ...formValues, accountId }).unwrap();
            }

            onSubmit();
        } catch (error) {
            const err = error as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        open: true,
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        open: true,
                        type: "general",
                        message: `Failed to ${title.toLowerCase()}`,
                    }
                })
            }
        }
        setInProgress(false);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormValues({
            ...formValues,
            [event.target.name]: event.target.value,
        })
    };

    const contactError = !formValues?.contact?.trim();
    const line2Error = !formValues?.line2?.trim();
    const line3Error = !formValues?.line3?.trim();
    const phoneError = !formValues?.phone?.trim();
    const descriptionError = !formValues?.description?.trim() || hasDupeDescription;
    const disabledSubmit = contactError || line2Error || line3Error || descriptionError || phoneError;

    return (
        <Dialog
            onClose={handleClose}
            maxWidth="sm"
            {...other}
        >
            <DialogTitle>
                {title}
            </DialogTitle>
            <DialogContent>
                <TextField
                    name='description'
                    required
                    fullWidth
                    autoFocus
                    margin="dense"
                    error={descriptionError}
                    helperText={hasDupeDescription ? 'Description already exists' : ''}
                    label='Description'
                    value={formValues.description}
                    inputProps={{ maxLength: 25 }}
                    disabled={inProgress}
                    onChange={handleChange}
                />
                <TextField
                    name='contact'
                    required
                    label='Onsite Contact Name'
                    margin="dense"
                    error={contactError}
                    value={formValues.contact}
                    fullWidth
                    inputProps={{ maxLength: 35 }}
                    disabled={inProgress}
                    onChange={handleChange}
                />
                <TextField
                    name='line1'
                    label='Suite/Level/Building/Unit Number/Letter'
                    margin="dense"
                    value={formValues.line1}
                    fullWidth
                    inputProps={{ maxLength: 35 }}
                    disabled={inProgress}
                    onChange={handleChange}
                />
                <TextField
                    name='line2'
                    label='Number, Street Name, Street Type'
                    margin="dense"
                    required
                    error={line2Error}
                    value={formValues.line2}
                    fullWidth
                    inputProps={{ maxLength: 35 }}
                    disabled={inProgress}
                    onChange={handleChange}
                />
                <TextField
                    name='line3'
                    label='Suburb, State, Postcode'
                    margin="dense"
                    error={line3Error}
                    required
                    value={formValues.line3}
                    fullWidth
                    inputProps={{ maxLength: 35 }}
                    disabled={inProgress}
                    onChange={handleChange}
                />
                <TextField
                    name='phone'
                    required
                    label='Contact Phone'
                    margin="dense"
                    error={phoneError}
                    value={formValues.phone}
                    fullWidth
                    inputProps={{ maxLength: 21 }}
                    disabled={inProgress}
                    onChange={handleChange}
                />
            </DialogContent>
            <DialogActions>
                {inProgress &&
                    <CircularProgress size={20} color={"secondary"} />
                }
                <Button onClick={handleClose} disabled={inProgress}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={disabledSubmit || inProgress}>Ok</Button>
            </DialogActions>
        </Dialog>
    );
}
